<template lang="pug">
  SspLayout.entry-channel-edit(area="core")
    SspSaveForm(@onSubmit="save" :saving="saving" :saveDisabled="!isValid")
      .form-horizontal
        .form-group
          label.col-sm-2.control-label Type
          .col-sm-10
            select.form-control(v-model="channelType" :disabled="channelId")
              option(value="web") WEB
              option(value="sms") SMS
        .form-group(v-if="channelType === 'web'")
          label.col-sm-2.control-label Alias
          .col-sm-10
            input.form-control(v-model="channel.alias" placeholder="Alias")
        .form-group(v-if="channelType === 'sms'")
          label.col-sm-2.control-label Phone Number
          .col-sm-10
            input.form-control(type="number" v-model="channel.phonenumber" placeholder="Phone number")
        .form-group(v-if="channelType === 'sms'")
          label.col-sm-2.control-label Prefix
          .col-sm-10
            input.form-control(type="number" v-model="channel.prefix" placeholder="Prefix")
        .form-group
          label.col-sm-2.control-label Invalid Message
          .col-sm-10
            input.form-control(v-model="channel.invalidMessage" placeholder="Invalid Message")

        .form-group(v-visible-for="['ROLE_POLICY_MANAGER']")
          label.col-sm-2.control-label Code Configuration
          .col-sm-10
            CodeConfigSelect(v-model="codeConfigId" :withGlobalConfigs="false")

        .form-group
          label.col-sm-2.control-label Owners
          .col-sm-10
            GenericTable
              tr(slot="thead")
                th Owner
                th
              tr(v-if="channel.owner" v-for="(o, index) in channel.owner")
                td
                  span(v-if="o.organization") {{o.organization.name}}
                td.actions
                  a.btn-trash(title="Remove owner" @click.prevent="removeOwner(index)")
              tr
                td
                  OrganizationSelect(v-model="channelOwner" ref="organizationSelect")
                td.actions
                  a.btn-trash(v-if="channel.owner.length > 1" title="Remove owner" @click.prevent="removeOwner(index)")
                  button.btn.btn-primary(type="button" @click.prevent="addOwner()") Add

        .form-group
          label.col-sm-2.control-label Translations
          .col-sm-10
            GenericTable(v-if="channel.translations")
              tr(slot="thead")
                th Country
                th Invalid Message
                th Actions
              tr(v-for="(t, index) in channel.translations.translation")
                td {{t.country | countryName}}
                td {{t.invalidMessage}}
                td.actions
                  a.btn-trash(title="Remove translation" @click.prevent="removeTranslation(index)")
              tr
                td
                  CountrySelect(v-model="country")
                td
                  input.form-control(v-if="country" v-model="invalidMessage")
                td.actions
                  a.btn-trash(v-if="channel.translations.translation.length > 1" title="Remove translation" @click.prevent="removeTranslation(index)")
                  button.mt10.btn.btn-primary(@click.prevent="addTranslation()") Add
</template>

<script>
import miniToastr from 'mini-toastr'
import Core from '@/service/apis/Core'
import { countryName } from '@/filters/filters'
import GenericTable from '@/components/common/GenericTable'
import CountrySelect from '@/components/ssp/CountrySelect'
import CodeConfigSelect from '@/components/ssp/CodeConfigSelect'
import OrganizationSelect from '@/components/ssp/OrganizationSelect'

export default {
  data: () => ({
    saving: false,
    disabled: false,
    channelOwner: null,
    country: null,
    invalidMessage: null,
    codeConfigId: null,
    channelType: null,
    channel: {
      codeConfig: {
        id: null
      },
      translations: {
        translation: []
      },
      owner: []
    }
  }),
  created() {
    if (this.channelId) {
      this.getEntryChannel()
    }
  },
  methods: {
    getEntryChannel() {
      Core.getEntryChannel(this.channelId)
        .then((resp) => {
          this.channel = Object.assign(this.channel, resp.data.webEntryChannel || resp.data.smsEntryChannel)
          this.channelType = resp.data.webEntryChannel ? 'web' : 'sms'

          if (this.channel.codeConfig) {
            this.codeConfigId = this.channel.codeConfig.id
          }
        })
        .catch((e) => {
          this.$emit('error', e)
        })
    },
    getChannelType() {
      const type = this.channelType === 'web' ? 'webEntryChannel' : 'smsEntryChannel'
      return type
    },
    save() {
      this.saving = true

      this.hasCodeConfigChange()
        .then(() => {
          this.doSave()
        })
    },
    doSave() {
      const req = {}
      req[this.getChannelType()] = this.channel
      req[this.getChannelType()].owner = this.channel.owner.map((o) => ({ organizationId: o.organization.id }))

      const call = this.channelId ? Core.updateEntryChannel(req) : Core.createEntryChannel(req)
      const action = this.channelId ? 'updated' : 'created'

      call
        .then((resp) => {
          miniToastr.success(`Channel was ${action}`)
          return resp.data.id
        })
        .then((channelId) => {
          if (!this.channelId && this.codeConfigId) {
            this.setCodeConfig(channelId, this.codeConfigId)
          }
        })
        .catch((e) => {
          this.$emit('error', e)
        })
        .then(() => {
          this.$router.push(this.channelId ? { name: 'entryChannel', params: { id: this.channelId } } : { name: 'entryChannels' })
          this.saving = false
        })
    },
    hasCodeConfigChange() {
      if (!this.codeConfigId && this.channel.codeConfig.id) {
        return this.$confirm(this.setDialogText('UNSET'))
          .then((confirm) => {
            if (confirm) {
              this.unsetCodeConfig(this.channelId)
            }
          })
      }
      if (this.channelId && this.codeConfigId && this.channel.codeConfig.id !== this.codeConfigId) {
        return this.$confirm(this.setDialogText('CHANGE'))
          .then((confirm) => {
            if (confirm) {
              this.setCodeConfig(this.channelId, this.codeConfigId)
            }
          })
      }

      return Promise.resolve(true)
    },
    setDialogText(action) {
      return {
        text: `Are you sure you want to <b>${action}</b> the code config on this channel?`,
        confirmButtonType: 'danger',
        confirmButtonText: 'YES',
        cancelButtonText: 'NO'
      }
    },
    addOwner() {
      const ownerObject = this.$refs.organizationSelect.getSelectedOrganization()
      const owner = { organization: { name: ownerObject.name, id: ownerObject.id } }
      this.channel.owner.push(owner)
      return owner
    },
    addTranslation() {
      const translation = { invalidMessage: this.invalidMessage, country: this.country.code }
      this.channel.translations.translation.push(translation)
      return translation
    },
    removeTranslation(index) {
      this.channel.translations.translation.splice(index, 1)
    },
    removeOwner(index) {
      this.channel.owner.splice(index, 1)
    },
    setCodeConfig(channelId, codeConfigId) {
      return Core.addCodeConfigToChannel(channelId, codeConfigId)
        .catch((e) => {
          this.$emit('error', e)
        })
    },
    unsetCodeConfig(channelId) {
      return Core.unassignCodeConfigFromChannel(channelId)
        .catch((e) => {
          this.$emit('error', e)
        })
    }
  },
  watch: {
    channelId() {
      this.getEntryChannel()
    }
  },
  computed: {
    isValid() {
      if (this.channelType === 'sms') {
        return this.channel.phonenumber && this.channelType
      }
      return this.channel.alias && this.channelType
    },
    channelId() {
      return parseInt(this.$route.params.id, 10)
    }
  },
  filters: {
    countryName
  },
  components: {
    GenericTable,
    CountrySelect,
    CodeConfigSelect,
    OrganizationSelect
  }
}
</script>

<style lang="scss">
.entry-channel-edit {
  .organization-select {
    background-color: white;
  }

  .country-select {
    background-color: white;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/core/validation/entrychannel/EntrychannelEdit.vue